import React, { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { metodoPago, tiendas, destino_pedido, ORDER_STATES, transportadora, bodegaFacturacion } from '../../assets/data/arrays'
import * as XLSX from 'xlsx'
import axiosInstance from '../../api/axiosInstance'
import { Button, Select } from '../ui'
import { FormField } from '../ui/Form'
import {
  DocumentArrowDownIcon,
  ShoppingBagIcon,
  CreditCardIcon,
  MapPinIcon,
  TruckIcon,
  CalendarIcon,
  CheckCircleIcon,
  Square3Stack3DIcon
} from '@heroicons/react/24/outline'
import ExportProgressModal from '../modals/ExportProgressModal'
import MultiSelectField from './MultiSelectField'
import ColumnSelector from './ColumnSelector'
import ExportProductSkuFilter from './ExportProductSkuFilter'
import { useColumnSelection } from '../../hooks/export/useColumnSelection'
import { getColumnDefinition } from '../../utils/exportColumns'
import { buildQueryParams, formatDateForFileName, type ExportFiltrosType } from '../../utils/exportHelpers'

interface ExportProgress {
  current: number
  total: number
  percentage: number
  currentPage: number
  totalPages: number
}

type ExportFormat = 'perProduct' | 'perOrderAggregated' | 'perPicking'

const Exportar: React.FC = () => {
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgress>({
    current: 0,
    total: 0,
    percentage: 0,
    currentPage: 0,
    totalPages: 0
  });

  const [exportFormat, setExportFormat] = useState<ExportFormat>('perProduct')

  const [filtros, setFiltros] = useState<ExportFiltrosType>({
    states: [{ value: 'asignado', label: 'Asignado' }],
    store_name: [
      { value: 'Artiq', label: 'Artiq' },
      { value: 'Bigcenter', label: 'Bigcenter' },
      { value: 'Distrito Cafetero', label: 'Distrito Cafetero' }
    ],
    payment_method: [],
    destination: [],
    transportadora: [],
    dateRange: { start: '', end: '' },
    id_market: '',
    sku: ''
  });

  // Hook para manejo de columnas seleccionadas
  const {
    selectedColumns,
    toggleColumn,
    selectAll,
    deselectAll,
    saveConfig
  } = useColumnSelection()

  useEffect(() => {
    const token = localStorage.getItem('refreshToken')
    if (!token) {
      toast.error('No hay token de autenticación', { position: 'top-center' })
      navigate('/login')
    }
  }, [navigate])

  // Helper para actualizar filtros
  const updateFilter = useCallback((field: keyof typeof filtros, value: any) => {
    setFiltros(prev => ({ ...prev, [field]: value }))
  }, [])

  // Generar archivo Excel con columnas seleccionadas
  const generateExcelFile = useCallback((allOrders: any[], columns: string[], format: ExportFormat) => {
    if (columns.length === 0) {
      toast.error('Debe seleccionar al menos una columna para exportar', { position: 'top-center' })
      return
    }

    // Obtener definiciones de columnas seleccionadas
    const selectedColumnDefs = columns
      .map(key => getColumnDefinition(key))
      .filter((def): def is NonNullable<typeof def> => def !== undefined)

    let excelData: Record<string, string | number>[]

    if (format === 'perProduct') {
      // Una fila por producto (formato actual)
      excelData = allOrders
        .map((order: any) => {
          return (order.products || []).map((product: any) => {
            const row: Record<string, string | number> = {}
            selectedColumnDefs.forEach(col => {
              row[col.label] = col.getValue(order, product)
            })
            return row
          })
        })
        .flat()
    } else if (format === 'perPicking') {
      // Formato picking: una fila por componente cuando el producto es combo
      excelData = allOrders
        .map((order: any) => {
          const rows: Record<string, string | number>[] = []
          const products = order.products || []

          products.forEach((product: any) => {
            const isCombo = product.es_combo === 1 || product.es_combo === '1'
            const hasComponents = Array.isArray(product.components) && product.components.length > 0

            if (!isCombo || !hasComponents) {
              // Producto simple: se comporta igual que perProduct
              const row: Record<string, string | number> = {}
              selectedColumnDefs.forEach(col => {
                row[col.label] = col.getValue(order, product)
              })
              rows.push(row)
              return
            }

            // Producto combo: generar una fila por componente
            const productQty = product.quantity || 1

            product.components.forEach((component: any) => {
              const mergedComponent: any = {
                ...product,
                ...component
              }

              const componentQty = component.cantidad || 1
              mergedComponent.quantity = productQty * componentQty

              const row: Record<string, string | number> = {}
              selectedColumnDefs.forEach(col => {
                row[col.label] = col.getValue(order, mergedComponent)
              })
              rows.push(row)
            })
          })

          return rows
        })
        .flat()
    } else {
      // Una fila por pedido: valores de producto agregados por coma
      excelData = allOrders.map((order: any) => {
        const row: Record<string, string | number> = {}
        const products = order.products || []

        selectedColumnDefs.forEach(col => {
          const values = products.map((product: any) => col.getValue(order, product))
          const cleanValues = Array.from(
            new Set(
              values
                .filter((v: string | number) => v !== null && v !== undefined && v !== '')
                .map((v: string | number) => String(v))
            )
          )
          row[col.label] = cleanValues.length > 0 ? cleanValues.join(', ') : ''
        })
        return row
      })
    }

    // Crear libro de Excel
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(excelData)

    // Ajustar el ancho de las columnas dinámicamente
    const colWidths = selectedColumnDefs.map(col => ({ wch: col.width || 15 }))
    ws['!cols'] = colWidths

    XLSX.utils.book_append_sheet(wb, ws, 'Pedidos')

    // Generar archivo Excel
    const fileName = `orders-${formatDateForFileName()}.xlsx`
    XLSX.writeFile(wb, fileName)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validar que haya columnas seleccionadas
    if (selectedColumns.length === 0) {
      toast.error('Debe seleccionar al menos una columna para exportar', { position: 'top-center' })
      return
    }

    try {
      setIsExporting(true)
      setShowProgressModal(true)

      const token = localStorage.getItem('accessToken')
      if (!token) {
        toast.error('No hay token de autenticación', { position: 'top-center' })
        navigate('/login')
        return
      }

      // Construir query params usando helper
      const queryParams = buildQueryParams(filtros, 1)

      // Primera consulta para obtener total
      const firstUrl = `/orders/all?${queryParams.toString()}`
      console.log('URL de la primera petición:', firstUrl)

      const firstResponse = await axiosInstance.get(firstUrl)
      const firstData = firstResponse.data

      if (!firstData.success) {
        throw new Error(firstData.message || 'Error al obtener pedidos')
      }

      const totalItems = firstData.meta?.totalItems || 0
      const totalPages = Math.ceil(totalItems / 80)

      if (totalItems === 0) {
        setShowProgressModal(false)
        toast.error('No se encontraron pedidos con los filtros seleccionados', { position: 'top-center' })
        return
      }

      // Inicializar progreso
      setExportProgress({
        current: firstData.data.length,
        total: totalItems,
        percentage: (1 / totalPages) * 100,
        currentPage: 1,
        totalPages
      })

      // Acumular primera página
      let allOrders = [...firstData.data]

      // Loop para obtener páginas restantes
      for (let page = 2; page <= totalPages; page++) {
        const pageQueryParams = buildQueryParams(filtros, page)
        const pageUrl = `/orders/all?${pageQueryParams.toString()}`

        const response = await axiosInstance.get(pageUrl)
        const data = response.data

        if (!data.success) {
          throw new Error(data.message || `Error al obtener página ${page}`)
        }

        // Acumular pedidos
        allOrders = [...allOrders, ...data.data]

        // Actualizar progreso
        setExportProgress({
          current: allOrders.length,
          total: totalItems,
          percentage: (page / totalPages) * 100,
          currentPage: page,
          totalPages
        })
      }

      // Cerrar modal y generar Excel
      setShowProgressModal(false)
      generateExcelFile(allOrders, selectedColumns, exportFormat)
      
      // Guardar configuración de columnas después de exportar exitosamente
      saveConfig(selectedColumns)
      
      toast.success(`Archivo Excel generado correctamente. ${allOrders.length} pedidos exportados con ${selectedColumns.length} columnas.`, { position: 'top-center' })
    } catch (error: any) {
      setShowProgressModal(false)
      console.error('Error:', error)
      
      if (error.response?.status === 401) {
        toast.error('Error de autenticación', { position: 'top-center' })
        navigate('/login')
      } else if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder a este recurso', { position: 'top-center' })
      } else {
        toast.error(error.response?.data?.message || error.message || 'Error al exportar los datos', { position: 'top-center' })
      }
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-center mb-2">
            <DocumentArrowDownIcon className="h-7 w-7 text-green-600 mr-3" />
            <h2>Exportar Pedidos</h2>
          </div>
          <p className="text-center text-gray-600 text-sm">
            Exporta pedidos a Excel con filtros personalizados. Los pedidos se obtienen de 80 en 80 para asegurar la exportación completa.
          </p>
        </div>

        {/* Formulario de Filtros */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <MultiSelectField
                label="Estados"
                helpText="Seleccione uno o más estados (opcional)"
                icon={CheckCircleIcon}
                options={ORDER_STATES.map(state => ({ label: state.label, value: state.value }))}
                value={filtros.states}
                onChange={(selected) => updateFilter('states', selected || [])}
                placeholder="Seleccionar estados..."
              />

              <MultiSelectField
                label="Tiendas"
                helpText="Seleccione una o más tiendas (opcional)"
                icon={ShoppingBagIcon}
                options={tiendas}
                value={filtros.store_name}
                onChange={(selected) => updateFilter('store_name', selected || [])}
                placeholder="Seleccionar tiendas..."
              />

              <MultiSelectField
                label="Métodos de Pago"
                helpText="Seleccione uno o más métodos (opcional)"
                icon={CreditCardIcon}
                options={metodoPago}
                value={filtros.payment_method}
                onChange={(selected) => updateFilter('payment_method', selected || [])}
                placeholder="Seleccionar métodos de pago..."
              />

              <MultiSelectField
                label="Destino del Pedido"
                helpText="Seleccione uno o más destinos (opcional)"
                icon={MapPinIcon}
                options={destino_pedido}
                value={filtros.destination}
                onChange={(selected) => updateFilter('destination', selected || [])}
                placeholder="Seleccionar destinos..."
              />

              <MultiSelectField
                label="Transportadoras"
                helpText="Seleccione una o más transportadoras (opcional)"
                icon={TruckIcon}
                options={transportadora.map(t => ({ label: t.label, value: t.value }))}
                value={filtros.transportadora}
                onChange={(selected) => updateFilter('transportadora', selected || [])}
                placeholder="Seleccionar transportadoras..."
              />

              <FormField
                label="Rango de Fechas"
                helpText="Seleccione fecha inicio y fin (opcional)"
                icon={CalendarIcon}
              >
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={filtros.dateRange.start}
                    onChange={(e) => updateFilter('dateRange', { ...filtros.dateRange, start: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  <input
                    type="date"
                    value={filtros.dateRange.end}
                    onChange={(e) => updateFilter('dateRange', { ...filtros.dateRange, end: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </FormField>

              <FormField
                label="Bodega de facturación"
                helpText="Seleccione una bodega (opcional)"
                icon={Square3Stack3DIcon}
              >
                <Select
                  value={filtros.id_market}
                  onChange={(e) => updateFilter('id_market', e.target.value)}
                  options={[
                    { value: '', label: 'Todas las bodegas' },
                    ...bodegaFacturacion
                  ]}
                  aria-label="Bodega de facturación"
                />
              </FormField>

              <FormField
                label="Formato de exportación"
                helpText="Estructura de las filas en el Excel"
              >
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  aria-label="Formato de exportación"
                >
                  <option value="perProduct">Una fila por producto (varias filas si hay varios productos)</option>
                  <option value="perOrderAggregated">Una fila por pedido (SKU y datos de producto separados por coma)</option>
                  <option value="perPicking">Formato picking (una fila por componente cuando hay kits)</option>
                </select>
              </FormField>

              <ExportProductSkuFilter
                sku={filtros.sku}
                onSkuChange={(sku) => updateFilter('sku', sku)}
              />

            </div>

            <Button
              type="submit"
              variant="primary"
              isLoading={isExporting}
              leftIcon={DocumentArrowDownIcon}
              fullWidth
              size="lg"
              disabled={isExporting || selectedColumns.length === 0}
            >
              {isExporting ? 'Exportando...' : 'Exportar Pedidos'}
            </Button>
          </form>
        </div>

        {/* Selector de Columnas */}
        <ColumnSelector
          selectedColumns={selectedColumns}
          onToggleColumn={toggleColumn}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
        />
      </div>

      {/* Modal de Progreso */}
      <ExportProgressModal
        isOpen={showProgressModal}
        progress={exportProgress}
        message="Obteniendo pedidos de la base de datos..."
      />
    </div>
  );
};

export default Exportar
